import React from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useCandidateFinancials from '../../hooks/useCandidateFinancials';

import '../feed/FeedItem.css';
import './CandidateFinancials.css';

/**
 * CandidateFinancials
 *
 * FEC campaign finance summary for one member, rendered as FeedItem cards so
 * it themes with everything else on the page. The legacy version used a
 * react-bootstrap Table with every color hardcoded to white, which only
 * worked back when there was a single dark theme.
 *
 * Money formatting notes:
 *  - Values are formatted as dollars via Intl, so 1234567.89 reads as
 *    $1,234,567.89 rather than the raw number the legacy table printed.
 *  - Missing and zero are kept distinct. The legacy code used `|| '0.00'`,
 *    which rendered a genuine zero and an absent field identically - on a
 *    finance report "contributed nothing to their own campaign" and "we do
 *    not have this number" are different claims.
 *
 * @param {Object} props
 * @param {string} props.candidateId - FEC candidate ID from member.fec_candidate_id
 */

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return null;
  }

  return currencyFormatter.format(numeric);
};

const formatCoverageDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  const month = utcDate.getMonth() + 1;
  const day = utcDate.getDate();
  const year = utcDate.getFullYear();
  return `${month}-${day}-${year}`;
};

function CandidateFinancials({ candidateId }) {
  const { t } = useTranslation('common');
  const { financials, loading, error } = useCandidateFinancials(candidateId);

  if (loading) {
    return <Spinner id="spinner" animation="grow" variant="light" />;
  }

  if (error) {
    return (
      <p className="member-detail-text">
        {t('common:errorPrefix', 'Error: {{message}}', { message: error.message })}
      </p>
    );
  }

  if (!financials) {
    return (
      <p className="member-detail-text">
        {t('common:noFinanceData', 'No campaign finance filings available.')}
      </p>
    );
  }

  const sections = [
    {
      key: 'raised',
      heading: t('common:totalRaised', 'Total Raised'),
      rows: [
        ['totalReceipts', 'Total receipts', financials.receipts],
        ['totalContributions', 'Total contributions', financials.contributions],
        ['itemizedIndividual', 'Itemized individual contributions', financials.individual_itemized_contributions],
        ['unitemizedIndividual', 'Unitemized individual contributions', financials.individual_unitemized_contributions],
        ['partyCommittee', 'Party committee contributions', financials.political_party_committee_contributions],
        ['otherCommittee', 'Other committee contributions', financials.other_political_committee_contributions],
        ['candidateContribution', 'Candidate contributions', financials.candidate_contribution],
      ],
    },
    {
      key: 'spent',
      heading: t('common:totalSpent', 'Total Spent'),
      rows: [
        ['totalDisbursements', 'Total disbursements', financials.disbursements],
        ['operatingExpenditures', 'Operating expenditures', financials.operating_expenditures],
        ['transfersToCommittees', 'Transfers to other authorized committees', financials.transfers_to_other_authorized_committee],
        ['fundraising', 'Fundraising', financials.fundraising_disbursements],
        ['exemptLegal', 'Exempt legal and accounting', financials.exempt_legal_accounting_disbursement],
        ['loanRepayments', 'Total loan repayments made', financials.loan_repayments_made],
        ['individualRefunds', 'Individual refunds', financials.refunded_individual_contributions],
        ['otherDisbursements', 'Other disbursements', financials.other_disbursements],
      ],
    },
    {
      key: 'cash',
      heading: t('common:cashSummary', 'Cash Summary'),
      rows: [
        ['endingCash', 'Ending cash on hand', financials.last_cash_on_hand_end_period],
        ['debtsOwedTo', 'Debts and loans owed to committee', financials.last_debts_owed_to_committee],
        ['debtsOwedBy', 'Debts and loans owed by committee', financials.last_debts_owed_by_committee],
      ],
    },
  ];

  const coverageEnd = formatCoverageDate(financials.coverage_end_date);
  const cycle = financials.cycle;

  return (
    <div className="candidate-financials">
      {/* FEC data lags by filing period, so a total with no date attached
          reads as current when it may be months old. */}
      {(cycle || coverageEnd) && (
        <p className="candidate-financials-coverage">
          {cycle && t('common:financeCycle', '{{cycle}} cycle', { cycle })}
          {cycle && coverageEnd ? ' · ' : ''}
          {coverageEnd &&
            t('common:financeThrough', 'Reported through {{date}}', { date: coverageEnd })}
        </p>
      )}

      {sections.map((section) => {
        const rows = section.rows
          .map(([key, fallback, value]) => ({
            key,
            label: t(`common:${key}`, fallback),
            amount: formatMoney(value),
          }))
          // A field the filing does not carry is dropped rather than shown as
          // zero, so the panel never asserts a number the FEC did not report.
          .filter((row) => row.amount !== null);

        if (rows.length === 0) return null;

        return (
          <div key={section.key} className="feed-item feed-item--default mb-4">
            <Card>
              <Card.Title id="billText" className="card-title p-3">
                {section.heading}
              </Card.Title>

              <Card.Body id="billBodyText">
                <dl className="candidate-financials-list">
                  {rows.map((row) => (
                    <div key={row.key} className="candidate-financials-row">
                      <dt className="candidate-financials-label">{row.label}</dt>
                      <dd className="candidate-financials-value">{row.amount}</dd>
                    </div>
                  ))}
                </dl>
              </Card.Body>
            </Card>
          </div>
        );
      })}

      <p className="candidate-financials-source">
        {t('common:financeSource', 'Source: Federal Election Commission')}
      </p>
    </div>
  );
}

export default CandidateFinancials;